import React, { useContext, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  LinearProgress,
  Paper,
  Switch,
  Typography,
} from "@mui/material";
import bkstApi from "../../api";
import { useNavigate } from "react-router";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import BDateTimePicker from "../../common/input/BDateTimePicker";
import {
  getDefaultQuoteExpiration,
  getDefaultQuoteExtension,
  hasOwnerAccess,
  isSalesTaxEnabled,
  shouldCollectSalesTax,
} from "../../common/util";
import QuoteFulfillmentEntry from "../../common/component/fulfillment/QuoteFulfillmentEntry";
import CustomerContactInfoButton from "../../common/component/CustomerContactInfoButton";
import BakerChat from "../../quote/component/action/BakerChat";
import CustomerDetailsButton from "../../common/component/CustomerDetailsButton";
import LineItems from "../../common/component/lineitem/LineItems";

export default function EditQuote(props) {
  const { user } = useContext(UserContext);
  let location = useLocation();

  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [fulfillment, setFulfillment] = useState({ type: "pickup" });
  const [exdate, setExdate] = useState("");

  const [collectSalesTax, setCollectSalesTax] = useState(false);

  const [errors, setErrors] = useState([]);

  const { reqId } = useParams();
  const [request, setRequest] = useState("");
  const [quote, setQuote] = useState("");

  const [loading, setLoading] = useState(true);

  const canToggleSalesTax =
    isSalesTaxEnabled(user.config) &&
    (hasOwnerAccess(user.role) || user.role === "manager");

  useEffect(() => {
    if (fulfillment.date) {
      if (!exdate || quote.status === "pending") {
        setExdate(getDefaultQuoteExpiration(fulfillment.date));
      } else if (quote.status === "expired" || moment(exdate).diff(moment(), "minutes") <= 30) {
        setExdate(getDefaultQuoteExtension(fulfillment.date));
      }
    }
  }, [fulfillment.date]);

  useEffect(() => {
    setLoading(true);

    bkstApi(`/my/quote/${reqId}`).then((res) => {
      setRequest(res.data.request);
      setQuote(res.data.quote);

      const quoteData = res.data.quote;
      setItems(quoteData.items || []);
      setFulfillment(quoteData.fulfillment);

      setExdate(quoteData.exdate);

      if (
        quoteData.settings?.collectSalesTax !== undefined ||
        quoteData.collectSalesTax !== undefined ||
        quoteData.settings?.includeSalesTax !== undefined
      ) {
        setCollectSalesTax(shouldCollectSalesTax(quoteData));
      } else {
        setCollectSalesTax(isSalesTaxEnabled(user.config));
      }

      setLoading(false);
    });
  }, [reqId, location.key]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const mode = e.nativeEvent?.submitter?.name === "save" ? "save" : "send";

    let payload = {
      mode,
      items,
      fulfillment,
      exdate,
      settings: { collectSalesTax },
      appVersion: process.env.REACT_APP_VERSION,
    };

    bkstApi
      .put(`/my/quote/${reqId}`, payload)
      .then((res) => {
        setLoading(true);
        navigate(`/quote/${reqId}`);
      })
      .catch((error) => {
        console.log(error);
        window.location.reload();
      });
  };

  if (loading) {
    return <LinearProgress />;
  } else if (request && quote.items) {
    const isFulfillmentDateInvalid =
      !fulfillment.date || moment(fulfillment.date).isBefore(moment(), "day");

    const isExdateInvalid = !exdate || moment(exdate).isBefore(moment(), "day");

    const isSendQuoteDisabled = items.length === 0 || isFulfillmentDateInvalid || isExdateInvalid;

    return (
      <Box sx={{ minHeight: "100vh", backgroundColor: "#FBEEE6" }}>
        <Container maxWidth="md">
          <Box py={6}>
            <form onSubmit={handleSubmit}>
              <Box>
                <Typography variant="h1" gutterBottom>
                  Edit Quote
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Complete all your changes. Then tap the Send Quote button to save changes & notify
                  the customer
                </Typography>
              </Box>
              <Box my={4}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ display: "flex", gap: "7px", alignItems: "center" }}>
                    {request.customer?.id ? (
                      <CustomerDetailsButton customer={request.customer} />
                    ) : (
                      <CustomerContactInfoButton
                        customer={{
                          firstName: request.firstName,
                          lastName: request.lastName,
                          name: request.name,
                        }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "4px",
                      alignItems: "center",
                      flexGrow: "1",
                      justifyContent: "flex-end",
                    }}
                  >
                    {request.src === "direct" && (
                      <BakerChat
                        request={request}
                        quote={quote}
                        status={quote.status}
                        disableAddImageToItem
                      />
                    )}
                  </Box>
                </Box>
                <LineItems items={items} onChange={(arr) => setItems(arr)} />
                {items.length > 0 && (
                  <Box>
                    <Box my={4}>
                      <QuoteFulfillmentEntry
                        fulfillment={fulfillment}
                        onChange={(o) => setFulfillment(o)}
                      />
                    </Box>
                    {canToggleSalesTax && (
                      <Box my={4}>
                        <Paper elevation={0}>
                          <Box px={2} py={1}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={collectSalesTax}
                                  onChange={(e) => setCollectSalesTax(e.target.checked)}
                                />
                              }
                              label="Collect Sales Tax"
                            />
                            <Typography variant="body2" color="textSecondary">
                              Turn off for wholesale or tax-exempt quotes. When on, sales tax
                              applies only to items marked taxable in the catalog.
                            </Typography>
                          </Box>
                        </Paper>
                      </Box>
                    )}
                    <Box mt={4} mb={2}>
                      <Box>
                        <FormControl variant="outlined" fullWidth>
                          <BDateTimePicker
                            value={exdate || null}
                            label="Expiration"
                            onChange={(d) => setExdate(d)}
                            disablePast
                            required
                            autoOk
                            maxDateTime={
                              (fulfillment.date &&
                                moment(fulfillment.date).endOf("day").toDate()) ||
                              null
                            }
                          />
                        </FormControl>
                      </Box>
                    </Box>
                    {errors && errors.length > 0 && (
                      <Box my={2}>
                        <Alert severity="error">{errors.join(". ")}</Alert>
                      </Box>
                    )}
                  </Box>
                )}
                <Box my={4}>
                  <Box sx={{ display: "flex", gap: "15px" }}>
                    {quote?.status === "pending" && (
                      <Button
                        name="save"
                        color="secondary"
                        fullWidth
                        type="submit"
                        variant="contained"
                        disableElevation
                        disabled={items.length === 0}
                      >
                        Save Draft
                      </Button>
                    )}
                    <Button
                      name="send"
                      fullWidth
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSendQuoteDisabled}
                    >
                      Send Quote
                    </Button>
                  </Box>
                  <Box my={2} textAlign={"center"}>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => navigate(`/quote/${reqId}`)}
                    >
                      Cancel Changes
                    </Button>
                  </Box>
                </Box>
              </Box>
            </form>
          </Box>
        </Container>
      </Box>
    );
  }
}
